import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AOS from 'aos';
import { REVIEWER_LOGO_MAP } from '../data/reviewers';
import { BookmarkFilledIcon, BookmarkOutlineIcon, SearchIcon, LockIcon } from '../components/Icons';
import ReviewerCardSkeleton from '../components/ReviewerCardSkeleton';
import { reviewerAPI, libraryAPI, examAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canAccessReviewer } from '../utils/subscription';
import giftIcon from '../Assets/giftIcon.png';
import crownIcon from '../Assets/crownIcon.png';

const AllReviewers = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [search, setSearch] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [inProgressMap, setInProgressMap] = useState({}); // { reviewerId: { attemptId, answeredCount, totalQuestions, progressPercent } }
  const [completedReviewerIds, setCompletedReviewerIds] = useState(new Set()); // reviewer IDs with submitted attempts
  const [libraryIds, setLibraryIds] = useState(new Set()); // reviewer IDs in user's library

  // Fetch reviewers and library on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [revRes, libRes, histRes] = await Promise.all([
          reviewerAPI.getAll(1, 50),
          isAuthenticated ? libraryAPI.get() : Promise.resolve({ success: true, data: [] }),
          isAuthenticated ? examAPI.getUserHistory(1, 20) : Promise.resolve({ success: true, data: [] }),
        ]);
        if (cancelled) return;
        if (revRes.success) {
          setReviewers(revRes.data);
          setPagination(revRes.pagination || null);
        }
        if (libRes.success) {
          setLibraryIds(new Set(libRes.data.map((r) => r._id)));
        }
        if (histRes.success) {
          const ipMap = {};
          const completed = new Set();
          histRes.data.forEach((attempt) => {
            const revId = String(attempt.reviewer?._id || attempt.reviewer);
            if (attempt.status === 'in_progress') {
              ipMap[revId] = {
                attemptId: attempt._id,
                answeredCount: attempt.progress?.answeredCount || 0,
                totalQuestions: attempt.progress?.totalQuestions || 0,
                progressPercent: attempt.progress?.progressPercent || 0,
              };
            } else if (attempt.status === 'submitted' || attempt.status === 'timed_out') {
              completed.add(revId);
            }
          });
          setInProgressMap(ipMap);
          setCompletedReviewerIds(completed);
        }
      } catch (err) {
        console.error('Failed to load reviewers:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    AOS.refresh();
  }, [reviewers]);

  const filtered = useMemo(() => {
    if (!search.trim()) return reviewers;
    const q = search.trim().toLowerCase();
    return reviewers.filter(
      (card) =>
        card.title.toLowerCase().includes(q) ||
        (card.type && String(card.type).toLowerCase().includes(q))
    );
  }, [search, reviewers]);

  const toggleLibrary = useCallback(async (id) => {
    if (togglingIds.has(id)) return;
    setTogglingIds((prev) => new Set(prev).add(id));
    const inLibrary = libraryIds.has(id);
    // Optimistic update
    setLibraryIds((prev) => {
      const next = new Set(prev);
      if (inLibrary) next.delete(id);
      else next.add(id);
      return next;
    });
    try {
      if (inLibrary) {
        await libraryAPI.remove(id);
      } else {
        await libraryAPI.add(id);
      }
    } catch (err) {
      // Revert on error
      console.error('Library toggle failed:', err);
      setLibraryIds((prev) => {
        const next = new Set(prev);
        if (inLibrary) next.add(id);
        else next.delete(id);
        return next;
      });
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [libraryIds, togglingIds]);

  const checkAccess = (reviewer) => canAccessReviewer(reviewer, { isAuthenticated, user });

  const freeReviewers = useMemo(() => filtered.filter((r) => r.access === 'free'), [filtered]);
  const premiumReviewers = useMemo(() => filtered.filter((r) => r.access !== 'free'), [filtered]);

  const renderCard = (card, index) => {
    const logoSrc = card.logo?.filename && REVIEWER_LOGO_MAP[card.logo.filename]
      ? REVIEWER_LOGO_MAP[card.logo.filename]
      : (card.logo?.path ?? null);
    const details = card.details || {};
    const inLibrary = libraryIds.has(card._id);
    const inProgressData = inProgressMap[String(card._id)];
    const inProgress = !!inProgressData;
    const hasCompleted = completedReviewerIds.has(String(card._id));
    const isFree = card.access === 'free';
    return (
      <div
        key={card._id}
        className="w-full max-w-[410.67px] min-w-0 bg-white border border-[#EFF0F6] rounded-[12px] p-[24px] text-left shadow-[0px_1px_8px_0px_#14142B0A] flex flex-col"
        data-aos="fade-up"
        data-aos-duration="500"
        data-aos-delay={100 + index * 50}
      >
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="flex items-center gap-[10px]">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="w-[40px] h-[40px] shrink-0 object-cover" />
            ) : (
              <div className="w-[40px] h-[40px] rounded bg-[#6E43B9] flex items-center justify-center text-white font-inter font-bold text-xs shrink-0">
                CSE
              </div>
            )}
            <span
              className={`font-inter font-regular text-[13px] px-[12px] py-[4px] rounded-[4px] ${isFree ? 'bg-[#FFF6D7] text-[#B28606]' : 'bg-[#6E43B91A] text-[#6E43B9]'
                }`}
            >
              {isFree ? 'Free' : 'Premium'}
            </span>
          </div>
          <div className="relative group">
            <button
              type="button"
              onClick={() => toggleLibrary(card._id)}
              className={`p-[7px] rounded-[4px] w-[40px] h-[40px] ${inLibrary ? 'bg-[#7D52CC1A]' : 'bg-[#F4F4F4]'
                } transition-colors flex items-center justify-center`}
              aria-label={inLibrary ? 'Remove from library' : 'Add to library'}
            >
              {inLibrary ? (
                <BookmarkFilledIcon className="w-[25px] h-[25px]" />
              ) : (
                <BookmarkOutlineIcon className="w-[25px] h-[25px]" />
              )}
            </button>
            <span
              role="tooltip"
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 py-1 px-2 font-inter font-medium text-[10px] text-white bg-[#616161E5] rounded-[4px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 align-middle"
            >
              {inLibrary ? 'Remove from library' : 'Add to library'}
            </span>
          </div>
        </div>
        <h2 className="font-inter text-[#45464E] font-medium text-[14px] mb-[16px]">
          {card.title}
        </h2>
        <p className="font-inter text-[#45464E] text-[13px] leading-[20px] mb-[16px] font-normal flex-1">
          {card.description?.full ?? ''}
        </p>
        <div className="flex flex-wrap items-center gap-[5px] text-[12px] text-[#45464E] mb-4">
          <span className="inline-flex items-center gap-1.5 font-inter font-normal not-italic">
            📝 {details.items ?? (card.examDetails?.itemsCount ? `${card.examDetails.itemsCount} items` : '—')}
          </span>
          <span className="text-[#45464E] font-inter font-normal not-italic">•</span>
          <span className="inline-flex items-center gap-1.5 font-inter font-normal not-italic">
            ⏱️ {details.duration ?? '—'}
          </span>
          {details.passingRate != null && (
            <>
              <span className="text-[#45464E] font-inter font-normal not-italic">•</span>
              <span className="inline-flex items-center gap-1.5 font-inter font-normal not-italic text-[#45464E]">
                🎯 Passing rate: {details.passingRate}
              </span>
            </>
          )}
          {details.accessLevel != null && (
            <>
              <span className="text-[#45464E] font-inter font-normal not-italic">•</span>
              <span className="inline-flex items-center gap-1.5 font-inter font-normal not-italic text-[#45464E]">
                📘 {details.accessLevel}
              </span>
            </>
          )}
        </div>
        {!checkAccess(card) ? (
          <div className="flex flex-col items-start gap-2">
            <button
              type="button"
              onClick={() => navigate('/dashboard/settings/update-subscription')}
              className="w-[205px] font-inter font-semibold text-[#421A83] text-[14px] sm:text-[16px] py-3 rounded-[8px] bg-[#EFE6FF] hover:opacity-95 transition-opacity flex items-center justify-center gap-2"
            >
              <LockIcon className="w-[18px] h-[21px] shrink-0" />
              Unlock Premium
            </button>
            {!isAuthenticated && (
              <p className="font-inter font-normal text-[12px] text-[#6C737F]">
                <Link to="/" className="text-[#6E43B9] font-semibold hover:underline">
                  Sign in
                </Link>
                {' '}to access
              </p>
            )}
          </div>
        ) : inProgress ? (
          <div className="flex items-start justify-start gap-6 w-full">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/exam/${card._id}`)}
              className={`font-inter font-semibold text-[14px] sm:text-[16px] py-3 px-4 rounded-[8px] hover:opacity-95 transition-opacity shrink-0 ${isFree ? 'bg-[#FFC92A] text-[#421A83]' : 'bg-[#6E43B9] text-white'
                }`}
            >
              Resume Exam
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between font-inter font-normal text-[10px] text-[#45464E] mb-1">
                <span>In Progress</span>
                <span>{inProgressData.progressPercent}%</span>
              </div>
              <div className="h-2 w-full rounded-[20px] bg-[#D9D9D9] overflow-hidden">
                <div
                  className={`h-full rounded-[20px] transition-all duration-300 ${isFree ? 'bg-[#FFC92A]' : 'bg-[#6E43B9]'
                    }`}
                  style={{ width: `${inProgressData.progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        ) : hasCompleted ? (
          <button
            type="button"
            onClick={() => navigate(`/dashboard/exam/${card._id}`)}
            className={`max-w-[140px] font-inter font-semibold text-[14px] sm:text-[16px] py-3 px-4 rounded-[8px] hover:opacity-95 transition-opacity ${isFree ? 'bg-[#FFC92A] text-[#421A83]' : 'bg-[#6E43B9] text-white'
              }`}
          >
            View Exam
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate(`/dashboard/exam/${card._id}`)}
            className={`max-w-[140px] font-inter font-semibold text-[14px] sm:text-[16px] py-3 px-4 rounded-[8px] hover:opacity-95 transition-opacity flex items-center justify-center gap-1 ${isFree ? 'bg-[#FFC92A] text-[#421A83]' : 'bg-[#6E43B9] text-white'
              }`}
          >
            {isFree ? 'Start Free →' : 'Start Exam →'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F4FF]">
      <main className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-20 pt-6 pb-16">
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-[40px]"
          data-aos="fade-up"
          data-aos-duration="500"
          data-aos-delay="0"
        >
          <h1 className="font-inter font-medium text-[20px] text-[#45464E] leading-tight">
            All Reviewers
          </h1>
          <div className="relative w-full max-w-[320px]">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" />
            <input
              type="search"
              placeholder="Search subject or exam type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-10 pr-4 py-2 font-inter font-normal text-base text-[#0F172A] placeholder:font-normal placeholder:text-[16px] placeholder:text-[#ABAFB1] border border-[#CFD3D4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6E43B9]/30 focus:border-[#6E43B9]"
              aria-label="Search subject or exam type"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-[24px] justify-items-center">
            {[...Array(6)].map((_, i) => (
              <ReviewerCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {freeReviewers.length > 0 && (
              <section className="mb-10">
                <div className="flex items-center gap-[16px] mb-[16px]" data-aos="fade-up" data-aos-duration="500">
                  <div className="w-[48px] h-[48px] rounded-[8px] bg-[#FFFAEA] flex items-center justify-center">
                    <img src={giftIcon} alt="" className="w-[24px] h-[24px] shrink-0 object-contain" />
                  </div>
                  <div>
                    <h2 className="font-inter font-medium text-[20px] text-[#45464E] mb-[4px] leading-tight">
                      Free Reviewers
                    </h2>
                    <p className="font-inter font-regular text-[14px] text-[#0F172ABF]">
                      Accessible for all users. Start reviewing now.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-[24px] justify-items-center">
                  {freeReviewers.map((card, index) => renderCard(card, index))}
                </div>
              </section>
            )}

            {premiumReviewers.length > 0 && (
              <section>
                <div className="flex items-center gap-[16px] mb-[16px]" data-aos="fade-up" data-aos-duration="500">
                  <div className="w-[48px] h-[48px] rounded-[8px] bg-[#EBE5FD] flex items-center justify-center">
                    <img src={crownIcon} alt="" className="w-[24px] h-[24px] shrink-0 object-contain" />
                  </div>                  <div>
                    <h2 className="font-inter font-medium text-[20px] text-[#45464E] mb-[4px] leading-tight">
                      Premium Reviewers
                    </h2>
                    <p className="font-inter font-regular text-[14px] text-[#0F172ABF]">
                      Unlocks full mock exams and section practice exams.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-[24px] justify-items-center">
                  {premiumReviewers.map((card, index) => renderCard(card, index))}
                </div>
              </section>
            )}
          </>
        )}
        {!loading && pagination && pagination.page < pagination.pages && (
          <div className="flex justify-center mt-8">
            <button
              type="button"
              disabled={loadingMore}
              onClick={async () => {
                setLoadingMore(true);
                try {
                  const res = await reviewerAPI.getAll(pagination.page + 1, 50);
                  if (res.success) {
                    setReviewers((prev) => [...prev, ...res.data]);
                    setPagination(res.pagination || null);
                  }
                } catch (err) {
                  console.error('Load more failed:', err);
                } finally {
                  setLoadingMore(false);
                }
              }}
              className="font-inter font-semibold text-[14px] text-[#421A83] py-3 px-6 rounded-[8px] border border-[#6E43B9] bg-white hover:bg-[#F5F4FF] transition-colors disabled:opacity-50"
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AllReviewers;
